// googleCalendar.js
// -----------------------------------------------------------------------------
// Google Calendar connect flow for the student side.
//
// SECURITY NOTE (read this):
// Your save-credential contract needs a refresh_token + client_secret. To get a
// refresh_token you must use the OAuth *authorization-code* flow and exchange the
// code at Google's token endpoint. That exchange requires the client_secret.
//
//   - If you exchange on the FRONTEND (exchangeCodeForTokens below), the
//     client_secret is bundled into the shipped JS and is publicly readable.
//     This works, but it is NOT recommended.
//   - RECOMMENDED: send only the short-lived `code` to your backend and let the
//     backend exchange it (secret stays server-side) and call save-credential.
//     See getAuthCode() — you can stop there and POST { code } to your API.
//
// Never hardcode the client_secret. It is read from an env var below.
// -----------------------------------------------------------------------------

import api from "../../api/api"; // <-- adjust relative path to your axios instance

// Configure via your build's env vars. (CRA: REACT_APP_*, Vite: import.meta.env.VITE_*)
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLEDRIVE_CLIENTID || "";
const GOOGLE_CLIENT_SECRET = process.env.REACT_APP_GOOGLEDRIVE_SECRET_KEY || ""; // ⚠️ frontend exchange only
const SCOPES = "https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/meetings.space.readonly https://www.googleapis.com/auth/userinfo.profile";
const TOKEN_URI = "https://oauth2.googleapis.com/token";
const GIS_SRC = "https://accounts.google.com/gsi/client";

const authHeaders = (token) => ({
  "Content-Type": "application/json",
  Authorization: `Token ${token}`,
});

const safe = async (promise) => {
  try {
    const res = await promise;
    return { ok: true, data: res?.data ?? null, error: null };
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.detail ||
      error?.message ||
      "Something went wrong. Please try again.";
    return { ok: false, data: null, error: message };
  }
};

// Load the Google Identity Services script once.
let gisPromise = null;
export function loadGoogleIdentity() {
  if (gisPromise) return gisPromise;
  gisPromise = new Promise((resolve, reject) => {
    if (window.google?.accounts?.oauth2) return resolve();
    const existing = document.querySelector(`script[src="${GIS_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("Failed to load Google Identity Services"))
      );
      return;
    }
    const s = document.createElement("script");
    s.src = GIS_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () =>
      reject(new Error("Failed to load Google Identity Services"));
    document.head.appendChild(s);
  });
  return gisPromise;
}

// Step 1 (safe, no secret): open Google consent and get a one-time auth code.
// This is the piece you'd keep if you move the exchange to the backend.
export function getAuthCode() {
  return new Promise((resolve, reject) => {
    if (!GOOGLE_CLIENT_ID)
      return reject(new Error("Missing REACT_APP_GOOGLE_CLIENT_ID"));
    if (!window.google?.accounts?.oauth2)
      return reject(new Error("Google Identity Services not loaded"));

    let resolved = false;

    // Detect popup close: when the popup closes, focus returns to the window
    const handleFocus = () => {
      if (!resolved) {
        resolved = true;
        window.removeEventListener("focus", handleFocus);
        reject(new Error("Google authentication popup closed without selection"));
      }
    };

    const client = window.google.accounts.oauth2.initCodeClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: SCOPES,
      ux_mode: "popup",
      // ensures a refresh_token is returned
      callback: (resp) => {
        if (resolved) return;
        resolved = true;
        window.removeEventListener("focus", handleFocus);
        if (resp?.error) reject(new Error(resp.error));
        else if (resp?.code) resolve(resp.code);
        else reject(new Error("No authorization code returned"));
      },
    });

    window.addEventListener("focus", handleFocus);
    client.requestCode();
  });
}

// Step 2 (⚠️ uses client_secret — move to backend when you can):
// exchange the auth code for access_token + refresh_token.
export async function exchangeCodeForTokens(code) {
  if (!GOOGLE_CLIENT_SECRET)
    throw new Error("Missing REACT_APP_GOOGLE_CLIENT_SECRET");

  const body = new URLSearchParams({
    code,
    client_id: GOOGLE_CLIENT_ID,
    client_secret: GOOGLE_CLIENT_SECRET,
    redirect_uri: "postmessage", // special value for GIS popup code flow
    grant_type: "authorization_code",
  });

  const res = await fetch(TOKEN_URI, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new Error(`Token exchange failed: ${res.status} ${detail}`);
  }
  return res.json(); // { access_token, refresh_token, expires_in, scope, ... }
}

// POST the credential to the backend save endpoint (your contract).
export function saveGoogleCredential(appToken, credential) {
  return safe(
    api.post("/students/google-meet/save-credential/", credential, {
      headers: authHeaders(appToken),
    })
  );
}

// Disconnect Google Calendar — DELETE the saved credential.
export async function disconnectGoogleCalendar(appToken) {
  return safe(
    api.delete("/students/google-meet/save-credential/", {
      headers: authHeaders(appToken),
    })
  );
}


// Full client-side flow: consent → code → tokens → save-credential. Returns { ok, error, data }.
export async function connectGoogleCalendar(appToken) {
  await loadGoogleIdentity();
  const code = await getAuthCode();
  const tokens = await exchangeCodeForTokens(code);

  const credential = {
    access_token: tokens.access_token,
    refresh_token: tokens.refresh_token,
    token_uri: TOKEN_URI,
    client_id: GOOGLE_CLIENT_ID,
    client_secret: GOOGLE_CLIENT_SECRET,
    scopes: SCOPES,
  };

  return await saveGoogleCredential(appToken, credential);
}

// --- RECOMMENDED server-side variant -----------------------------------------
// If/when the backend adds a code-exchange endpoint, use this instead. The
// client_secret then never touches the frontend.
//
// export async function connectGoogleCalendarViaBackend(appToken) {
//   await loadGoogleIdentity();
//   const code = await getAuthCode();
//   return safe(
//     api.post("/students/google-meet/save-credential/", { code,
//       redirect_uri: "postmessage" }, { headers: authHeaders(appToken) })
//   );
// }